import type { Theme } from '@trinity/chat-components/types/theme';

export const FALLBACK_COLORS = {
  primary: '#e5e9f2',
  primaryHover: '#d3d3d3',
  secondary: '#d3d3d3',
  secondaryHover: '#d3d3d3',
  tertiary: '#D5F2F1',
  tertiaryHover: '#D5F2F1',
  border: '#e3e3e3',
  borderLight: '#f2f2f2',
  background: '#ffffff',
  backgroundLight: '#f7f8fc',
  text: '#515151',
  textSecondary: '#626262',
  error: '#E12E40',
  iconColor: '#5C73AD',
  iconColorSecondary: '#323232',
  iconColorTertiary: '#2F8B89',
} as const;

export const THEME_SHADOWS = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
} as const;

export const THEME = {
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
  },
  borderRadius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    md: '15px',
    base: '16px',
    lg: '18px',
    xl: '20px',
  },
  colors: FALLBACK_COLORS,
  boxShadow: THEME_SHADOWS,
} as const;

export const getThemeValue = <T,>(
  getter: (theme: Theme.Theme) => T,
  fallback: T,
  theme?: Theme.Theme,
): T => {
  if (!theme) return fallback;
  try {
    return getter(theme) ?? fallback;
  } catch {
    return fallback;
  }
};

export const getWhiteLabelThemeValue = <T,>(
  wlGetter: (wl: Theme.WhiteLabelPalette) => T | null | undefined,
  standardGetter: (theme: Theme.Theme) => T | null | undefined,
  fallback: T,
  theme?: Theme.Theme,
): T => {
  if (!theme) return fallback;
  try {
    // Try white label palette first
    if (theme.palette?.wl) {
      const wlValue = wlGetter(theme.palette.wl);
      if (wlValue != null) return wlValue;
    }
    // Fall back to standard palette
    return standardGetter(theme) ?? fallback;
  } catch {
    return fallback;
  }
};

export const getThemeColor = {
  primary: (theme?: Theme.Theme): string => getWhiteLabelThemeValue(
    (wl) => wl.primary?.main,
    (t) => t.palette?.gray?.light5,
    FALLBACK_COLORS.primary,
    theme,
  ),
  primaryHover: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.gray?.light1,
    FALLBACK_COLORS.primaryHover,
    theme,
  ),
  secondary: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.gray?.light1,
    FALLBACK_COLORS.secondary,
    theme,
  ),
  secondaryHover: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.gray?.light1,
    FALLBACK_COLORS.secondaryHover,
    theme,
  ),
  tertiary: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.green?.light3,
    FALLBACK_COLORS.tertiary,
    theme,
  ),
  tertiaryHover: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.green?.light3,
    FALLBACK_COLORS.tertiaryHover,
    theme,
  ),
  border: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.gray?.light4,
    FALLBACK_COLORS.border,
    theme,
  ),
  borderLight: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.gray?.light2,
    FALLBACK_COLORS.borderLight,
    theme,
  ),
  background: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.white?.main,
    FALLBACK_COLORS.background,
    theme,
  ),
  backgroundLight: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.gray?.light7,
    FALLBACK_COLORS.backgroundLight,
    theme,
  ),
  text: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.text?.primary,
    FALLBACK_COLORS.text,
    theme,
  ),
  textSecondary: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.gray?.dark1,
    FALLBACK_COLORS.textSecondary,
    theme,
  ),
  error: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.red?.main,
    FALLBACK_COLORS.error,
    theme,
  ),
  iconColor: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.purple?.light8,
    FALLBACK_COLORS.iconColor,
    theme,
  ),
  iconColorSecondary: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.black?.main,
    FALLBACK_COLORS.iconColorSecondary,
    theme,
  ),
  iconColorTertiary: (theme?: Theme.Theme): string => getThemeValue(
    (t) => t.palette?.green?.dark2,
    FALLBACK_COLORS.iconColorTertiary,
    theme,
  ),
};

export const getFocusShadow = (theme: Theme.Theme | undefined, fallbackColor = '0, 123, 255'): string => {
  const primaryColor = getThemeColor.primary(theme);

  // Extract RGB values from hex color or use fallback
  const rgb = primaryColor.startsWith('#')
    ? primaryColor.slice(1).match(/.{2}/g)?.map((x: string) => parseInt(x, 16)).join(', ') || fallbackColor
    : fallbackColor;
  return `0 0 0 3px rgba(${rgb}, 0.25)`;
};

